import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Mail, Phone, Clock, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { TricolorStripe } from '../components/TricolorStripe';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { t } = useLanguage();
  const c = t.contact;
  const [searchParams] = useSearchParams();
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    full_name: '',
    organization: '',
    email: '',
    phone: '',
    sport: '',
    province: '',
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const org = searchParams.get('org');
    const sport = searchParams.get('sport');
    const message = searchParams.get('message');
    if (org || sport || message) {
      setFormData((prev) => ({
        ...prev,
        ...(org ? { organization: org } : {}),
        ...(sport ? { sport } : {}),
        ...(message ? { message } : {}),
      }));
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 250);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(c.form.success);
      setFormData({
        full_name: '',
        organization: '',
        email: '',
        phone: '',
        sport: '',
        province: '',
        message: '',
      });
    } catch (error) {
      toast.error(c.form.error);
    } finally {
      setIsLoading(false);
    }
  };

  const sports = ['Soccer', 'Basketball', 'Volleyball', 'Football', 'Rugby', 'Multi-sport', 'Other'];
  const provinces = ['QC', 'ON', 'BC', 'AB', 'MB', 'SK', 'NB', 'NS', 'PE', 'NL', 'Other'];

  return (
    <div className="min-h-screen bg-[#0D0D0D]" data-testid="contact-page">
      {/* Hero */}
      <section className="relative h-[35vh] flex items-center justify-center bg-[#0D0D0D]">
        <div className="relative z-10 text-center px-6">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-bebas text-5xl md:text-6xl lg:text-7xl text-white tracking-tight"
          >
            {c.hero.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="font-dm text-base text-white/80 mt-4 max-w-2xl mx-auto"
          >
            {c.hero.subtitle}
          </motion.p>
        </div>
      </section>

      <TricolorStripe />

      {/* Contact Section */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left - Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-bebas text-3xl md:text-4xl text-[#0D0D0D] tracking-tight mb-8">
                {c.info.title}
              </h2>

              <div className="space-y-8">
                {/* Location */}
                <div>
                  <p className="font-dm text-xs tracking-[4px] text-[#CE2B37] uppercase mb-2">
                    {c.info.location}
                  </p>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#6B6B6B] mt-0.5" />
                    <p className="font-dm text-sm text-[#0D0D0D]" data-testid="contact-business-address">
                      4003 Boulevard Robert<br />
                      {t.footer.address[1]}
                    </p>
                  </div>
                </div>

                {/* Email */}
                <div>
                  <p className="font-dm text-xs tracking-[4px] text-[#CE2B37] uppercase mb-2">
                    {c.info.email}
                  </p>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-[#6B6B6B]" />
                    <p className="font-dm text-sm text-[#0D0D0D]">
                      {t.footer.email}
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <p className="font-dm text-xs tracking-[4px] text-[#CE2B37] uppercase mb-2">
                    {c.info.phone}
                  </p>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-[#6B6B6B]" />
                    <p className="font-dm text-sm text-[#0D0D0D]">
                      {t.footer.phone}
                    </p>
                  </div>
                </div>

                {/* Hours */}
                <div>
                  <p className="font-dm text-xs tracking-[4px] text-[#CE2B37] uppercase mb-2">
                    {c.info.hours}
                  </p>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[#6B6B6B]" />
                    <p className="font-dm text-sm text-[#0D0D0D]">
                      {c.info.hoursValue}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tricolor Divider */}
              <div className="flex my-8 w-24">
                <div className="flex-1 h-[3px] bg-[#009246]" />
                <div className="flex-1 h-[3px] bg-white border-y border-[#E8E8E8]" />
                <div className="flex-1 h-[3px] bg-[#CE2B37]" />
              </div>

              {/* B2B Callout */}
              <div className="bg-[#C8B89A]/20 border border-[#C8B89A]/30 p-6" data-testid="b2b-callout">
                <h3 className="font-bebas text-xl text-[#0D0D0D] tracking-wide mb-2">
                  {c.b2b.title}
                </h3>
                <p className="font-dm text-sm text-[#6B6B6B] leading-relaxed">
                  {c.b2b.body}
                </p>
              </div>
            </motion.div>

            {/* Right - Form */}
            <motion.div
              ref={formRef}
              initial={{ opacity: 0, x: 16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder={c.form.fullName}
                    required
                    className="bg-white border border-[#E8E8E8] text-[#0D0D0D] placeholder:text-[#6B6B6B] font-dm text-sm px-4 py-4 w-full focus:outline-none focus:border-[#CE2B37] transition-colors duration-200"
                    data-testid="contact-full-name"
                  />
                  <input
                    type="text"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    placeholder={c.form.organization}
                    required
                    className="bg-white border border-[#E8E8E8] text-[#0D0D0D] placeholder:text-[#6B6B6B] font-dm text-sm px-4 py-4 w-full focus:outline-none focus:border-[#CE2B37] transition-colors duration-200"
                    data-testid="contact-organization"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={c.form.email}
                    required
                    className="bg-white border border-[#E8E8E8] text-[#0D0D0D] placeholder:text-[#6B6B6B] font-dm text-sm px-4 py-4 w-full focus:outline-none focus:border-[#CE2B37] transition-colors duration-200"
                    data-testid="contact-email"
                  />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={c.form.phone}
                    className="bg-white border border-[#E8E8E8] text-[#0D0D0D] placeholder:text-[#6B6B6B] font-dm text-sm px-4 py-4 w-full focus:outline-none focus:border-[#CE2B37] transition-colors duration-200"
                    data-testid="contact-phone"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <select
                    name="sport"
                    value={formData.sport}
                    onChange={handleChange}
                    className="bg-white border border-[#E8E8E8] text-[#6B6B6B] font-dm text-sm px-4 py-4 w-full focus:outline-none focus:border-[#CE2B37] transition-colors duration-200 appearance-none"
                    data-testid="contact-sport"
                  >
                    <option value="">{c.form.sport}</option>
                    {sports.map((sport) => (
                      <option key={sport} value={sport}>{sport}</option>
                    ))}
                  </select>
                  <select
                    name="province"
                    value={formData.province}
                    onChange={handleChange}
                    className="bg-white border border-[#E8E8E8] text-[#6B6B6B] font-dm text-sm px-4 py-4 w-full focus:outline-none focus:border-[#CE2B37] transition-colors duration-200 appearance-none"
                    data-testid="contact-province"
                  >
                    <option value="">{c.form.province}</option>
                    {provinces.map((prov) => (
                      <option key={prov} value={prov}>{prov}</option>
                    ))}
                  </select>
                </div>

                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={c.form.message}
                  required
                  rows={5}
                  className="bg-white border border-[#E8E8E8] text-[#0D0D0D] placeholder:text-[#6B6B6B] font-dm text-sm px-4 py-4 w-full focus:outline-none focus:border-[#CE2B37] transition-colors duration-200 resize-none"
                  data-testid="contact-message"
                />

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#CE2B37] text-white font-bebas text-lg tracking-wider py-4 hover:bg-[#a82230] transition-colors duration-200 disabled:opacity-50"
                  data-testid="contact-submit-btn"
                >
                  {isLoading ? '...' : c.form.submit}
                </button>

                <p className="font-dm text-xs text-[#6B6B6B] text-center">
                  {c.form.note}
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Google Map */}
      <section className="bg-[#0D0D0D]" data-testid="contact-map-section">
        <iframe
          title="DM Sports Mass Canada — Montréal"
          src="https://www.google.com/maps?q=4003+Boulevard+Robert,+Montr%C3%A9al,+QC+H1Z+4H6&output=embed"
          className="w-full h-[420px] border-0 block"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
          data-testid="contact-google-map"
        />
      </section>

      <TricolorStripe />

      {/* FAQ Section */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-bebas text-4xl md:text-5xl text-[#0D0D0D] tracking-tight text-center mb-12"
          >
            {c.faq.title}
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <Accordion type="single" collapsible className="space-y-4">
              {c.faq.items.map((item, i) => (
                <AccordionItem
                  key={item.q}
                  value={`item-${i}`}
                  className="bg-white border border-[#E8E8E8] px-6"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="font-dm text-sm text-[#0D0D0D] text-left py-5 hover:no-underline">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="font-dm text-sm text-[#6B6B6B] leading-relaxed pb-5">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
